import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "universal-cookie";
import { handleResponseCode } from "../../../app/utilities/helpers";
import GetProxy from "../../../app/GetProxy";

const cookies = new Cookies();
const proxy = GetProxy();

const ASSET_TYPE_API_TO_UI = {
  SERVER: "Server",
  FIREWALL: "Firewall",
  NETWORK: "Network",
  OTHERS: "Others",
};

const NETWORK_API_TO_UI = {
  IT_ENTERPRISE: "IT Enterprise",
  TELCO: "Telco",
};

// The list sends a finished scan's breakdown as `severity: { Critical, ... }`;
// flatten it to the per-count fields the Completed column reads.
const severityCounts = (severity) => {
  const critical = Number(severity?.Critical) || 0;
  const high = Number(severity?.High) || 0;
  const medium = Number(severity?.Medium) || 0;
  const low = Number(severity?.Low) || 0;
  const info = Number(severity?.Info) || 0;
  return {
    criticalCount: critical,
    highCount: high,
    mediumCount: medium,
    lowCount: low,
    infoCount: info,
    totalSeverityCount: critical + high + medium + low + info,
  };
};

// A CIS Benchmark scan is graded rather than scored: instead of `severity` the
// list sends `compliance: { Passed, Failed, Warning, Total }`. Flatten it the
// same way, so the Completed column can draw whichever breakdown the row has.
const complianceCounts = (compliance) => {
  const passed = Number(compliance?.Passed) || 0;
  const failed = Number(compliance?.Failed) || 0;
  const warning = Number(compliance?.Warning) || 0;
  return {
    passedCount: passed,
    failedCount: failed,
    warningCount: warning,
    // `Total` is the benchmark's own count of checks, which is what the
    // number beside the bar means. It only falls back to the three bands
    // when the list leaves it out.
    totalComplianceCount:
      Number(compliance?.Total) || passed + failed + warning,
    // Which benchmark was run, for the details popup.
    complianceAuditId: compliance?.auditId || "",
  };
};

// Map a /sassp/scans/list response row to the shape the table/columns expect.
const transformScanItem = (item) => ({
  id: item.id,
  // Cycle ids and legacy asset ids come from different sequences and can
  // collide (for example, cycle 63 alongside asset 63). Keep the namespaces
  // distinct because this value is used as the table's React row key.
  tableRowId:
    item.scanCycleId != null
      ? `cycle-${item.scanCycleId}`
      : `asset-${item.id}`,
  // The list is now one row per scan cycle rather than one per asset, so an
  // asset with both a vulnerability and a CIS cycle running sends two rows
  // that share `id` and `jobId`. `scanCycleId` is what tells those two
  // rows apart — the row identity everything per-row (table keys, "find my
  // row after an action") should use instead of `id`. Older payloads carry
  // no such field, so it falls back to `id`, which is still unique when
  // there is only one cycle per asset.
  scanCycleId: item.scanCycleId ?? item.id,
  // The scan APIs key off their own ids: pause/resume take the scan id and
  // the stored report takes the job id. Neither falls back to the row id —
  // that is the asset's, and sending it would address the wrong thing.
  // `externalScanId` is the per-cycle spelling; `scanId` is kept as a
  // fallback for a payload still on the older, one-row-per-asset shape.
  scanId: item.externalScanId ?? item.scanId ?? "",
  jobId: item.jobId ?? item.reportJobId ?? "",
  scanStatus: item.scanStatus || "",
  // The list carries the running scan's completion figure, so the In
  // Progress bar draws straight from the row.
  progress: Number(item.progress) || 0,
  dccName: item.dccName || "",
  hostName: item.hostName || "",
  ipAddress: item.ip || "",
  assetType: ASSET_TYPE_API_TO_UI[item.assetType] || item.assetType || "",
  location: item.location || "",
  environment: item.environment || "",
  network: NETWORK_API_TO_UI[item.network] || item.network || "",
  scanType: item.scanType || "",
  assetMode: item.assetMode || "",
  status: item.status || "",
  requestedBy: item.requestedBy || item.createdBy || "",
  requestedOn: item.requestedOn || item.createdDt || "",
  approvedBy: item.approvedBy || "",
  approvedOn: item.decidedOn || "",
  dismissedOn: item.decidedOn || "",
  // The run trail behind the In Progress popup. Kept off the columns that
  // read it, so a row that carries only the short API spelling still fills
  // the popup in.
  scanInitiatedBy: item.scanInitiatedBy || item.initiatedBy || "",
  scanInitiatedOn: item.scanInitiatedOn || item.initiatedOn || "",
  scanStoppedBy: item.scanStoppedBy || item.stoppedBy || "",
  scanStoppedOn: item.scanStoppedOn || item.stoppedOn || "",
  // When a finished scan ran itself out, as opposed to being stopped.
  scanEndedOn: item.scanEndedOn || item.endedOn || item.completedOn || "",
  comment: item.rejectionReason || item.comment || "",
  // Why a scan was cancelled or failed. Its own field rather than the
  // comment, which carries the asset's approval note.
  reason: item.reason || "",
  // Already a formatted "DD/MM/YYYY HH:mm:ss" string from the list API,
  // so it is displayed as sent rather than run through the epoch formatter.
  lastScannedDate: item.lastScannedDate || "",
  ...severityCounts(item.severity),
  ...complianceCounts(item.compliance),
});

// The backend answers errors with a ResponseEntity envelope
// ({ body, statusCode, statusCodeValue, errorCode }) rather than a bare HTTP
// error, so a 200-shaped response can still be a failure.
const isErrorEnvelope = (data) =>
  !!data && typeof data === "object" && Number(data.statusCodeValue) >= 400;

const isHtmlString = (str) =>
  typeof str === "string" && /^\s*<(!doctype|html|head|body)/i.test(str);

// `body` holds the human-readable reason on the error envelope.
const envelopeMessage = (data, fallback) => {
  if (typeof data === "string" && !isHtmlString(data) && data.trim()) {
    return data.trim();
  }
  if (
    typeof data?.body === "string" &&
    !isHtmlString(data.body) &&
    data.body.trim()
  ) {
    return data.body.trim();
  }
  const message =
    data?.message || data?.error || data?.errorMessage || data?.detail;
  if (typeof message === "string" && !isHtmlString(message) && message.trim()) {
    return message.trim();
  }
  return fallback;
};

// A failed call may answer with JSON, a plain-text reason, HTML or an empty body;
// never let that throw over the top of the real error.
const parseResponseBody = async (response) => {
  try {
    const text = await response.text();
    if (!text || !text.trim()) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text.trim();
    }
  } catch {
    return null;
  }
};

const authHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  "DTOP-API-TOKEN": cookies.get("apitoken"),
  USERNAME: cookies.get("username"),
});

export const getScanListApi = createAsyncThunk(
  "post/getScanListApi",
  async ({ pageNumber, itemsPerPage, payload }) => {
    const apiToken = cookies.get("apitoken");
    const username = cookies.get("username");
    const response = await fetch(
      `${proxy}/sassp/scans/list/${pageNumber}/${itemsPerPage}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "DTOP-API-TOKEN": apiToken,
          USERNAME: username,
        },
        body: JSON.stringify(payload),
      },
    );
    handleResponseCode(response);
    const data = await response.json();
    return data;
  },
);

// Pending tab: hand the credentials to Tenable and start the scan. The backend
// SSH-checks the host first, so a rejection here is usually a connectivity or
// credential problem and its message is worth showing verbatim.
export const launchScanApi = createAsyncThunk(
  "post/launchScanApi",
  async ({ assetId, userName, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${proxy}/sassp/scans/launch`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          assetId: String(assetId ?? ""),
          userName,
          password,
        }),
      });
      handleResponseCode(response);
      const data = await parseResponseBody(response);
      if (!response.ok || isErrorEnvelope(data)) {
        const message = envelopeMessage(data, "Failed to initiate scan");
        if (data && typeof data === "object" && data.navigationKey) {
          return rejectWithValue({
            ...data,
            message,
          });
        }
        return rejectWithValue(message);
      }
      return data;
    } catch (error) {
      console.error("Error:", error);
      return rejectWithValue(error?.message || "Failed to initiate scan");
    }
  },
);

// In Progress tab: pause and resume share a shape, so they share a thunk and
// only differ in the path segment.
export const updateScanStateApi = createAsyncThunk(
  "post/updateScanStateApi",
  async ({ scanId, action }, { rejectWithValue }) => {
    const endpoint = action === "resume" ? "resumeScan" : "pauseScan";
    try {
      const response = await fetch(
        `${proxy}/tenableOneScanLaunchController/${endpoint}/${scanId}`,
        {
          method: "POST",
          headers: authHeaders(),
        },
      );
      handleResponseCode(response);
      const data = await parseResponseBody(response);
      if (!response.ok || isErrorEnvelope(data)) {
        return rejectWithValue(
          envelopeMessage(data, `Failed to ${action} scan`),
        );
      }
      return {
        scanId,
        action,
        ...(typeof data === "object" ? data : {}),
      };
    } catch (error) {
      console.error("Error:", error);
      return rejectWithValue(error?.message || `Failed to ${action} scan`);
    }
  },
);

// The extension used when the backend sends no Content-Disposition to name
// the file for us.
const REPORT_FILE_EXTENSION = {
  PDF: "pdf",
  CSV: "csv",
};

/* A CIS Benchmark scan is graded rather than scored: it comes back as checks
   that passed, failed or warned, with no severities at all. The scan type on
   the row is what says which of the two a completed scan is. */
export const isComplianceScanType = (scanType) =>
  String(scanType || "")
    .toLowerCase()
    .includes("cis");

// The two kinds of report the store holds, as the download endpoint spells
// them.
export const reportScanType = (scanType) =>
  isComplianceScanType(scanType) ? "CIS" : "VULNERABILITY";

/* What a report download is tracked under while it is in flight.

   `jobId` alone used to be enough to say which row's download to spin — one
   asset, one job. Now that the list holds one row per scan cycle, two rows
   for the same asset can share a `jobId` (a CIS cycle and a vulnerability
   cycle both scanning the same host), so downloading one would also spin the
   other's button. Folding the scan type into the key is what tells the two
   apart; a caller with no scan type to hand (or one from before this existed)
   still gets the bare job id it always did. */
export const reportRequestKey = ({ jobId, scanType }) => {
  if (!jobId) {
    return "";
  }
  return scanType ? `${jobId}::${reportScanType(scanType)}` : jobId;
};

// The short code a scan type earns in the filename, in place of the longer
// spelling `reportScanType` sends the endpoint.
const REPORT_TYPE_CODE = {
  CIS: "CIS",
  VULNERABILITY: "VA",
};

/* The report is filed under the DCC it belongs to and the kind of scan it
   is, so the download reads `SASSP_<CIS|VA>_<dcc>.<ext>`. The SASSP prefix
   says which application the file came from, since it ends up in a
   downloads folder alongside reports from other tools; the type code tells
   a CIS Benchmark report apart from a vulnerability one without spelling
   either out in full. Anything a filesystem would object to is folded away
   and runs of whitespace become single underscores, leaving a name that
   still reads as the DCC it came from. A DCC made up entirely of such
   characters sanitises down to nothing, which falls back to the job id the
   same way a missing name does. */
export const scanReportFileName = ({
  dccName,
  jobId,
  scanType,
  format = "PDF",
}) => {
  const extension =
    REPORT_FILE_EXTENSION[String(format).toUpperCase()] || "pdf";
  const typeCode = REPORT_TYPE_CODE[reportScanType(scanType)];
  const safeDcc = String(dccName || "")
    // An allowlist rather than a list of what Windows and macOS object to:
    // a DCC name only ever carries words, digits and separators, so
    // everything outside that is safe to drop.
    .replace(/[^A-Za-z0-9 ._-]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  return `SASSP_${typeCode}_${safeDcc || jobId}.${extension}`;
};

/* The stored report comes back as a binary stream, so it is read as a blob and
   the filename comes from the DCC name, falling back to Content-Disposition
   when the row has none.

   Two things pick the file out: `scanType`, because a graded scan's report and
   a scored one's are built differently, and `format`, the rendering the dialog
   offers. The endpoint spells the scan type in capitals and the format in
   lower case. */
export const downloadScanReportApi = createAsyncThunk(
  "get/downloadScanReportApi",
  async ({ jobId, scanType, dccName, format = "PDF" }, { rejectWithValue }) => {
    const reportFormat = String(format).toUpperCase();
    try {
      const response = await fetch(
        `${proxy}/tenableOneScanLaunchController/downloadStoredReport/${jobId}?scanType=${reportScanType(
          scanType,
        )}&format=${reportFormat.toLowerCase()}`,
        {
          method: "GET",
          headers: {
            "DTOP-API-TOKEN": cookies.get("apitoken"),
            USERNAME: cookies.get("username"),
          },
        },
      );
      handleResponseCode(response);
      if (!response.ok) {
        return rejectWithValue("Failed to download scan report");
      }
      const blob = await response.blob();
      /* The DCC name wins over whatever the backend labelled the stream:
               Content-Disposition carries the Tenable-side job name, which is
               not what people go looking for in their downloads folder. It is
               still the fallback when the row has no DCC to go on. */
      const named = scanReportFileName({
        dccName,
        jobId,
        scanType,
        format: reportFormat,
      });
      const fallbackName = scanReportFileName({
        jobId,
        scanType,
        format: reportFormat,
      });
      if (named !== fallbackName) {
        return { blob, fileName: named };
      }
      const contentDisposition =
        response.headers?.get?.("Content-Disposition") || "";
      const fileNameMatch = contentDisposition.match(
        /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i,
      );
      return {
        blob,
        fileName: fileNameMatch
          ? decodeURIComponent(fileNameMatch[1])
          : fallbackName,
      };
    } catch (error) {
      console.error("Error:", error);
      return rejectWithValue(
        error?.message || "Failed to download scan report",
      );
    }
  },
);

const initialState = {
  scanListData: { data: [], totalCount: 0 },
  scanListLoading: false,
  // Whichever list request went out last. Switching tabs fires a new one
  // while the previous tab's is still in flight, and the two can come back
  // out of order — without this the slower, older response would land on
  // top of the tab the user is now looking at.
  scanListRequestId: "",
  launchScanLoading: false,
  scanActionLoading: false,
  reportDownloadingId: "",
  error: "",
  apiError: false,
};

export const AssetScanSlice = createSlice({
  name: "AssetScanSlice",
  initialState,

  reducers: {
    setErrorAlert(state) {
      state.apiError = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getScanListApi.pending, (state, action) => {
        state.scanListRequestId = action.meta?.requestId ?? "";
        state.scanListLoading = true;
        state.apiError = false;
      })
      .addCase(getScanListApi.fulfilled, (state, action) => {
        // A superseded request: its rows belong to a tab or a page the
        // user has already moved off.
        if (state.scanListRequestId !== (action.meta?.requestId ?? "")) {
          return;
        }
        state.scanListLoading = false;
        const items = (action.payload?.data || []).map(transformScanItem);
        state.scanListData = {
          data: items,
          totalCount: action.payload?.totalCount ?? items.length,
        };
      })
      .addCase(getScanListApi.rejected, (state, action) => {
        if (state.scanListRequestId !== (action.meta?.requestId ?? "")) {
          return;
        }
        state.scanListLoading = false;
        state.scanListData = { data: [], totalCount: 0 };
        state.error = action.error?.message || "Failed to load scan list";
        state.apiError = true;
      })

      .addCase(launchScanApi.pending, (state) => {
        state.launchScanLoading = true;
      })
      .addCase(launchScanApi.fulfilled, (state) => {
        state.launchScanLoading = false;
      })
      .addCase(launchScanApi.rejected, (state, action) => {
        state.launchScanLoading = false;
        state.error =
          (typeof action.payload === "string"
            ? action.payload
            : action.payload?.message || action.payload?.body) ||
          "Failed to initiate scan";
      })

      .addCase(updateScanStateApi.pending, (state) => {
        state.scanActionLoading = true;
      })
      .addCase(updateScanStateApi.fulfilled, (state) => {
        state.scanActionLoading = false;
      })
      .addCase(updateScanStateApi.rejected, (state, action) => {
        state.scanActionLoading = false;
        state.error = action.payload || "Failed to update scan";
      })

      .addCase(downloadScanReportApi.pending, (state, action) => {
        state.reportDownloadingId = reportRequestKey(action.meta.arg || {});
      })
      .addCase(downloadScanReportApi.fulfilled, (state) => {
        state.reportDownloadingId = "";
      })
      .addCase(downloadScanReportApi.rejected, (state, action) => {
        state.reportDownloadingId = "";
        state.error = action.payload || "Failed to download scan report";
      });
  },
});

export const { setErrorAlert } = AssetScanSlice.actions;
export default AssetScanSlice.reducer;
